import { Flex, Input, Form, FormInstance, Select, message } from "antd";
import {
  useJsApiLoader,
  GoogleMap,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import {
  SetStateAction,
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
} from "react";
import { venueType } from "../../models/event";
import { Dispatch } from "react";
import { useParams } from "react-router";
import Loader from "../../components/Loader";
import googleImg from "../../assets/google_icon.png";
import { IconSearch } from "@tabler/icons-react";
import { useAPI } from "../../hooks/useAPI";
import { ENDPOINTS } from "../../constants/endpoints.constant";
import { getCall } from "../../constants/apicalls.constant";

const containerStyle = {
  width: "100%",
  height: "315px",
  borderRadius: "15px",
};

const center = {
  lat: 28.6139,
  lng: 77.209,
};

export type VenueMode = "city-pick" | "normal";

const Venue = ({
  venue,
  mode,
  setVenue,
  form,
  fieldPrefix = "",
  scheduleVenueLocked = false,
  venueLocked = false,
}: {
  venue: venueType | null;
  form: FormInstance;
  mode: VenueMode;
  setVenue: Dispatch<SetStateAction<venueType | null>>;
  fieldPrefix?: string;
  scheduleVenueLocked?: boolean;
  venueLocked?: boolean;
}) => {
  const cityLocked = scheduleVenueLocked;
  const f = (name: string) => `${fieldPrefix}${name}`;
  const { id } = useParams();
  const [mapCenter, setMapCenter] = useState(center);
  const [markerPosition, setMarkerPosition] = useState(center);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const [citiesList, setCitiesList] = useState<any[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const { trigger: triggerFetchCities } = useAPI(ENDPOINTS.get_cities);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showCityPicker = mode === "city-pick";
  const showVenueForm = mode === "normal" && Boolean(venue);

  const fetchCities = async (searchVal: string) => {
    try {
      setCitiesLoading(true);
      const res: any = await triggerFetchCities(getCall({ search: searchVal }));
      if (res) {
        let list: any[] = [];
        if (Array.isArray(res)) list = res;
        else if (Array.isArray(res.data)) list = res.data;
        else if (Array.isArray(res?.data?.data)) list = res.data.data;
        setCitiesList(list);
      }
    } catch (err) {
      console.error("Failed to load cities", err);
    } finally {
      setCitiesLoading(false);
    }
  };

  useEffect(() => {
    fetchCities("");
  }, []);

  const handleSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchCities(value);
    }, 400);
  };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey:
      import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
      import.meta.env.VITE_APP_GOOGLE_API_URL,
    libraries: ["places"],
  });

  const extractAddressComponent = (
    components: google.maps.GeocoderAddressComponent[],
    type: string,
  ): string => {
    const component = components.find((c) => c.types.includes(type));
    return component?.long_name || "";
  };

  const resolveCityName = async (
    googleCity: string,
  ): Promise<string | undefined> => {
    if (!googleCity) return undefined;

    const matchedCity = citiesList.find((c: any) => {
      const cityName = typeof c === "string" ? c : c.name || c.city || "";
      return cityName.toLowerCase() === googleCity.toLowerCase();
    });

    if (matchedCity) {
      return typeof matchedCity === "string"
        ? matchedCity
        : matchedCity.name || matchedCity.city;
    }

    try {
      const res: any = await triggerFetchCities(
        getCall({ search: googleCity }),
      );
      const dataArr: any[] = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

      if (dataArr.length > 0) {
        setCitiesList((prev) => {
          const merged = [...prev];
          dataArr.forEach((item: any) => {
            const name =
              typeof item === "string" ? item : item.name || item.city || "";
            if (
              !merged.some(
                (m) =>
                  (
                    typeof m === "string" ? m : m.name || m.city || ""
                  ).toLowerCase() === name.toLowerCase(),
              )
            ) {
              merged.push(item);
            }
          });
          return merged;
        });

        const matched = dataArr.find((c: any) => {
          const cityName = typeof c === "string" ? c : c.name || c.city || "";
          return cityName.toLowerCase() === googleCity.toLowerCase();
        });

        if (matched) {
          return typeof matched === "string"
            ? matched
            : matched.name || matched.city;
        }
      }
    } catch (err) {
      console.error(err);
    }

    return undefined;
  };

  const matchOrFetchCity = async (
    googleCity: string,
    venueData: venueType,
    formFields: Record<string, unknown>,
  ) => {
    if (cityLocked) {
      // Keep city as-is; only update address details
      form.setFieldsValue(formFields);
      setVenue((prev) =>
        prev ? { ...prev, ...venueData, city: prev.city } : venueData,
      );
      return;
    }

    if (!googleCity) {
      form.setFieldsValue({ ...formFields, [f("city")]: undefined });
      setVenue((prev) =>
        prev
          ? { ...prev, ...venueData, city: "" }
          : { ...venueData, city: "" },
      );
      return;
    }

    const finalCity = await resolveCityName(googleCity);
    const resolvedCity = finalCity || "";

    form.setFieldsValue({
      ...formFields,
      [f("city")]: resolvedCity || undefined,
    });
    setVenue((prev) =>
      prev
        ? { ...prev, ...venueData, city: resolvedCity }
        : { ...venueData, city: resolvedCity },
    );
  };

  const handleCityPick = async (
    googleCity: string,
    placeName: string,
    lat?: number,
    lng?: number,
    state: string = "",
    country: string = "",
    postal: string = ""
  ) => {
    if (cityLocked) return;

    const finalCity = await resolveCityName(googleCity || placeName);

    if (!finalCity) {
      message.error("Please select a valid city from the suggestions.");
      return;
    }

    form.setFieldsValue({
      [f("city")]: finalCity,
      [f("venue_name")]: undefined,
      [f("line1")]: undefined,
      [f("line2")]: undefined,
    });

    setVenue({
      name: finalCity,
      city: finalCity,
      place_id: "",
      lat: lat != null ? `${lat}` : "",
      lng: lng != null ? `${lng}` : "",
      line1: "",
      line2: "",
      state: state,
      postal: postal,
      type: "locality",
      country: country,
    });

    if (lat != null && lng != null) {
      setMapCenter({ lat, lng });
      setMarkerPosition({ lat, lng });
    }
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();

    if (!place || !place.geometry) {
      alert("No details available for the input.");
      return;
    }

    const components = place.address_components ?? [];
    const lat = place.geometry.location?.lat();
    const lng = place.geometry.location?.lng();

    const googleCity: string =
      extractAddressComponent(components, "locality") ||
      extractAddressComponent(components, "administrative_area_level_3") ||
      extractAddressComponent(components, "administrative_area_level_2") ||
      extractAddressComponent(components, "administrative_area_level_1") ||
      place.name ||
      "";

    const state = extractAddressComponent(components, "administrative_area_level_1");
    const country = extractAddressComponent(components, "country");
    const postal = extractAddressComponent(components, "postal_code");

    if (showCityPicker) {
      void handleCityPick(googleCity, place.name || googleCity, lat, lng, state, country, postal);
      return;
    }

    const [firstLine, ...rest] = place.formatted_address?.split(",") ?? [];
    const line1 = firstLine.trim();
    const line2 = rest.join(",").trim();

    const venueData: venueType = {
      name: place.name || "",
      city: googleCity,
      place_id: place.place_id!,
      type: place.types?.[0] || "",
      line1,
      line2,
      lat: `${lat}`,
      lng: `${lng}`,
      state: extractAddressComponent(
        components!,
        "administrative_area_level_1",
      ),
      country: extractAddressComponent(components!, "country"),
      postal: extractAddressComponent(components!, "postal_code"),
    };

    const formFields = {
      [f("venue_name")]: place.name,
      [f("line1")]: line1,
      [f("line2")]: line2,
    };

    void matchOrFetchCity(googleCity, venueData, formFields);

    if (lat && lng) {
      setMapCenter({ lat, lng });
      setMarkerPosition({ lat, lng });
    }
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (showCityPicker) return;

    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();

    if (lat == null || lng == null) {
      return;
    }

    setMapCenter({ lat, lng });
    setMarkerPosition({ lat, lng });

    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const placeResult = results[0];
        const components = placeResult.address_components ?? [];
        const [firstLine, ...rest] =
          placeResult.formatted_address?.split(",") ?? [];
        const line1 = firstLine.trim();
        const line2 = rest.join(",").trim();
        const googleCity =
          extractAddressComponent(components, "locality") ||
          extractAddressComponent(components, "administrative_area_level_3") ||
          extractAddressComponent(components, "administrative_area_level_2") ||
          extractAddressComponent(components, "administrative_area_level_1") ||
          "";

        const venueData: venueType = {
          name: placeResult.address_components?.[0]?.long_name || "",
          place_id: placeResult.place_id!,
          type: placeResult.types?.[0] || "",
          line1,
          line2,
          lat: `${lat}`,
          lng: `${lng}`,
          state: extractAddressComponent(
            components,
            "administrative_area_level_1",
          ),
          postal: extractAddressComponent(components, "postal_code"),
          country: extractAddressComponent(components, "country"),
          city: googleCity,
        };

        const formFields = {
          [f("venue_name")]: placeResult.address_components?.[0]?.long_name,
          [f("line1")]: line1,
          [f("line2")]: line2,
        };

        void matchOrFetchCity(googleCity, venueData, formFields);
      } else {
        console.warn("No address found");
      }
    });
  };

  useLayoutEffect(() => {
    if (!id) return;

    if (venue) {
      form.setFieldsValue({
        [f("venue_name")]: venue.name,
        [f("line1")]: venue.line1,
        [f("line2")]: venue.line2,
        [f("city")]: venue.city || undefined,
      });
      if (venue.lat && venue.lng) {
        const lat = parseFloat(venue.lat);
        const lng = parseFloat(venue.lng);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          setMapCenter({ lat, lng });
          setMarkerPosition({ lat, lng });
        }
      }
    }
  }, [venue, id, form, fieldPrefix, mode]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <Loader />;

  return (
    <>
      <Flex vertical>
        <div className="mb-[30px]">
          <Autocomplete
            onLoad={(autocomplete) => {
              autocompleteRef.current = autocomplete;
            }}
            types={[]}
            onPlaceChanged={handlePlaceChanged}
          >
            <Input
              type="text"
              disabled={venueLocked}
              readOnly={venueLocked}
              placeholder={
                venueLocked
                  ? "Location locked"
                  : showCityPicker
                    ? "Start typing to search for a city..."
                    : "Start typing to search venue"
              }
              prefix={
                <img
                  src={googleImg}
                  alt="google"
                  width={15}
                  style={{ marginRight: 8 }}
                />
              }
              suffix={<IconSearch size={16} color="#DBDCDE" />}
              className={venueLocked ? "!bg-[#f9fafb] !text-[#9ca3af]" : ""}
            />
          </Autocomplete>
        </div>

        {showCityPicker && venue?.city && (
          <div className="mb-[30px] p-3 bg-[#f0f9ff] border border-[#bae6fd] rounded-lg">
            <span className="text-[#0369a1] font-medium">
              Selected Location:{" "}
            </span>
            <span className="text-[#0c4a6e]">
              {venue.name}{" "}
              {venue.city && venue.city !== venue.name ? `(${venue.city})` : ""}
            </span>
          </div>
        )}

        {showVenueForm ? (
          <div className="flex w-full md:gap-[30px] flex-wrap md:flex-nowrap">
            <div className="w-full">
              <Form.Item name={f("venue_name")} rules={[{ required: true }]}>
                <Input
                  placeholder="Venue Name"
                  disabled
                  className="!bg-white !text-black"
                />
              </Form.Item>
              <Form.Item name={f("line1")}>
                <Input
                  placeholder="Venue Address"
                  disabled={venueLocked}
                  className={venueLocked ? "!bg-white !text-black" : ""}
                />
              </Form.Item>
              <Form.Item
                name={f("city")}
                rules={[{ required: true, message: "Please select a city" }]}
              >
                <Select
                  showSearch
                  placeholder="Select/Search City"
                  loading={citiesLoading}
                  disabled={cityLocked}
                  onSearch={handleSearch}
                  filterOption={false}
                  onChange={(val) => {
                    if (cityLocked) return;
                    setVenue((prev) => {
                      if (!prev) return null;
                      return { ...prev, city: val };
                    });
                  }}
                  options={citiesList.map((c: any) => {
                    const cityName =
                      typeof c === "string" ? c : c.name || c.city || "";
                    return {
                      label: cityName,
                      value: cityName,
                    };
                  })}
                />
              </Form.Item>
              <Form.Item name={f("line2")}>
                <Input.TextArea
                  rows={8}
                  disabled
                  className="!bg-white !text-black"
                  placeholder="Venue Address"
                />
              </Form.Item>
            </div>

            <GoogleMap
              mapContainerStyle={containerStyle}
              center={mapCenter}
              zoom={14}
              onClick={venueLocked ? undefined : handleMapClick}
              options={{ gestureHandling: venueLocked ? "none" : "auto" }}
            >
              <Marker position={markerPosition} />
            </GoogleMap>
          </div>
        ) : null}
      </Flex>
    </>
  );
};

export default Venue;
